import { useEffect, useMemo, useRef, useState } from "react";
import { saveJson } from "./radarUi";
import { rtctApi } from "services/rtctApi";
import {
  echoList,
  finite,
  formatMm as mm,
  levelAt,
  sgtTime as sgt,
} from "utils/radarDiagnostics";
import {
  HIDDEN_VALUE,
  PRIVATE_SETTINGS,
  arrivalText,
  captureExport,
  captureFileName,
  echoListSupported,
  echoReplyAnswer,
  hasSpectrum,
  maskSettingsText,
  parseSettingsDump,
  settingMeaning,
  settingsDumpOf,
  spectrumRows,
} from "utils/radarStream";

// Radar live: everything one capture holds besides its spectrum - what its
// raw-output window brought, the radar's echo list, its settings dump, the
// settings in force - and a download of the whole capture with our analysis.
const CFG = [
  ["blind_mm", "blind zone", "mm"],
  ["range_mm", "range", "mm"],
  ["cal_mm", "calibration offset", "mm"],
  ["inst_mm", "install height", "mm"],
  ["cf_ms", "sampling period", "ms"],
  ["push_ms", "push output", "ms"],
  ["ver", "sensor firmware", ""],
];
// Per-capture fields of firmware .55, on the stream row or the stored capture.
const ARRIVAL_KEYS = ["raw_mode", "n_fft", "n_adc", "n_thr", "end"];
const MODES = {
  stream: "streamed",
  burst: "from a rich-wake burst",
  waterfall: "the waterfall's copy",
};

const signedMm = (v) => {
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? "+" : r < 0 ? "−" : "±"}${mm(Math.abs(r), 1)}`;
};
const cfgValue = (key, value, unit) =>
  key === "push_ms" && value === 0
    ? "off"
    : typeof value === "number" && unit
      ? `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}`
      : String(value);
const noEchoListText = (answer) =>
  `This radar's firmware does not provide an echo list${answer ? `: it answers the query (AT+PEAKPREDICT) with “${answer}”` : ""}.`;

// The radar's settings dump as a table: each setting, its value and what it
// means. The radar's own identifier (SID) stays hidden until asked for, here
// and in the raw text.
export function SettingsDump({ dump, reveal, onReveal }) {
  const parsed = useMemo(() => parseSettingsDump(dump.text), [dump.text]);
  const values = useMemo(
    () => Object.fromEntries(parsed.rows.map((r) => [r.key, r.value])),
    [parsed],
  );
  return (
    <>
      {parsed.rows.length > 0 ? (
        <div className="field-stream-table field-stream-settings">
          <table className="field-table" aria-label="Radar settings">
            <thead>
              <tr>
                <th scope="col">Setting</th>
                <th scope="col">Value</th>
                <th scope="col">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {parsed.rows.map((row, i) => {
                const hidden = PRIVATE_SETTINGS.includes(row.key);
                return (
                  <tr key={`${row.key}-${i}`}>
                    <th scope="row">{row.key}</th>
                    <td>
                      {hidden && !reveal ? HIDDEN_VALUE : row.value || "—"}
                      {hidden && (
                        <button
                          type="button"
                          className="field-stream-reveal"
                          aria-label={`${reveal ? "Hide" : "Show"} ${row.key}`}
                          onClick={() => onReveal(!reveal)}
                        >
                          {reveal ? "Hide" : "Show"}
                        </button>
                      )}
                    </td>
                    <td>{settingMeaning(row.key, row.value, values) || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="field-caption">
          The reply holds no settings in the AT+NAME=value form; its text is
          below.
        </p>
      )}
      {parsed.other.length > 0 && (
        <p className="field-caption">
          Also in the reply: {parsed.other.join(" · ")}
        </p>
      )}
      <details className="field-radar-details">
        <summary>Raw text</summary>
        <pre className="field-raw" aria-label="Settings dump text">
          {reveal ? dump.text : maskSettingsText(dump.text)}
        </pre>
      </details>
      <p className="field-caption">
        The radar's reply to its settings query
        {dump.answer ? ` (it answered: ${dump.answer})` : ""}.
      </p>
    </>
  );
}

export default function RadarCaptureInspector({
  mac,
  capture,
  detail,
  analysis,
  echoUnsupported = false,
}) {
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const points = useMemo(
    () =>
      spectrumRows(capture)
        .filter((r) => r.db !== null)
        .map((r) => ({ r: r.d, db: r.db })),
    [capture],
  );
  if (!capture)
    return (
      <div className="field-radar-empty">
        Nothing to inspect yet: the newest capture appears here, or the one
        picked on the waterfall.
      </div>
    );
  const data = detail?.data || null;
  const list = echoList(data?.peaks ?? capture.peaks);
  const pick = analysis?.pick?.mm ?? null;
  const dump = settingsDumpOf(data);
  const cfg = data?.context?.cfg;
  // How the radar answered the echo-list query for this capture; without an
  // answer of its own, the panel's verdict from the newest one.
  const reply = data?.context?.peaks_reply ?? null;
  const support = echoListSupported(reply);
  const noEchoList = support === false || (support === null && echoUnsupported);
  const contents = [
    hasSpectrum(capture) && "spectrum",
    capture.has_thr && "threshold",
    capture.has_adc && "beat signal",
    (list?.echoes.length > 0 || support === true) && "echo list",
  ].filter(Boolean);
  const mode = capture.mode ?? data?.mode;
  const seq = finite(capture.seq) ?? finite(data?.seq);
  // Firmware .56: a burst capture says which burst and where in it.
  const burst = data?.burst ?? capture.burst ?? null;
  const meta = [
    capture.id !== null && capture.id !== undefined
      ? `capture ${capture.id}`
      : null,
    mode ? MODES[mode] || `mode ${mode}` : null,
    burst && finite(burst.i) !== null
      ? `${finite(burst.i) + 1} of ${finite(burst.n) ?? "?"} in burst ${burst.id}`
      : null,
    seq !== null ? `sequence ${seq}` : null,
    contents.length ? `holds ${contents.join(", ")}` : "holds no radar data",
    finite(data?.byte_size) !== null
      ? `${(data.byte_size / 1000).toFixed(1)} kB as received`
      : null,
    data?.crc_ok === true
      ? "checksum OK"
      : data?.crc_ok === false
        ? "failed the radar's CRC-16/MODBUS check (the vendor tool would drop it)"
        : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const arrival = Object.fromEntries(
    ARRIVAL_KEYS.map((k) => [k, capture[k] ?? data?.[k] ?? null]),
  );
  // The context as stored, with the radar's identifier hidden unless shown.
  const getconfig = data?.context?.getconfig;
  const context =
    data && !reveal && typeof getconfig?.text === "string"
      ? {
          ...data.context,
          getconfig: { ...getconfig, text: maskSettingsText(getconfig.text) },
        }
      : data?.context;

  const download = async () => {
    setBusy(true);
    let full = data;
    let problem = detail?.error || "";
    if (!full && capture.id !== null && capture.id !== undefined) {
      try {
        full = await rtctApi.radarCapture(capture.id);
        problem = "";
      } catch (e) {
        problem = e.message;
      }
    }
    if (capture.fromWaterfall)
      problem =
        "No stored capture for this time: the stream record is the waterfall's copy (0.5 dB steps).";
    saveJson(
      captureFileName(mac, capture),
      captureExport({
        mac,
        capture,
        detail: full,
        detailError: problem,
        analysis,
        exportedAt: new Date().toISOString(),
      }),
    );
    if (alive.current) setBusy(false);
  };

  return (
    <div className="field-stream-inspector">
      <div className="field-stream-inspector-head">
        <div>
          <p className="field-caption" style={{ margin: 0 }}>
            {sgt(capture.t)} · {meta}
          </p>
          {!capture.fromWaterfall && (
            <p className="field-stream-arrived">
              What arrived: {arrivalText({ ...capture, ...arrival })}.
            </p>
          )}
        </div>
        <button
          type="button"
          className="field-button"
          disabled={busy}
          onClick={download}
        >
          {busy ? "Preparing the file…" : "Download capture (JSON)"}
        </button>
      </div>
      <div className="field-stream-inspector-grid">
        <div role="group" aria-labelledby="field-stream-echoes">
          <h4 id="field-stream-echoes" className="field-stream-subhead">
            Radar's echo list
          </h4>
          {list?.echoes.length ? (
            <div className="field-stream-table">
              <table className="field-table" aria-label="Radar's echo list">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Distance</th>
                    <th scope="col">Strength</th>
                    <th scope="col">Level</th>
                    <th scope="col">From pick</th>
                  </tr>
                </thead>
                <tbody>
                  {list.echoes.map((e) => {
                    const level = levelAt(points, e.rangeMm);
                    return (
                      <tr key={e.rank}>
                        <td>{e.rank}</td>
                        <td>{mm(e.rangeMm, 1)}</td>
                        <td>
                          {e.strength === null
                            ? "—"
                            : e.strength.toLocaleString(undefined, {
                                maximumFractionDigits: 1,
                              })}
                        </td>
                        <td>
                          {level === null ? "—" : `${level.toFixed(1)} dB`}
                        </td>
                        <td>
                          {pick === null ? "—" : signedMm(e.rangeMm - pick)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="field-caption">
                As the radar listed them, strongest first; strength in the
                radar's own units, distance as reported; level is the spectrum's
                at that distance.
              </p>
            </div>
          ) : noEchoList ? (
            <div className="field-radar-empty" style={{ marginTop: 0 }}>
              {noEchoListText(support === false ? echoReplyAnswer(reply) : "")}
            </div>
          ) : (
            <p className="field-caption">
              {reply
                ? "The radar listed no echoes in this capture."
                : data
                  ? "No echo list with this capture. Tick Echo list in Radar rich data on Node Control to stream it."
                  : "No echoes listed with this capture."}
            </p>
          )}
        </div>
        <div role="group" aria-labelledby="field-stream-dump">
          <h4 id="field-stream-dump" className="field-stream-subhead">
            Settings dump
          </h4>
          {dump ? (
            <SettingsDump dump={dump} reveal={reveal} onReveal={setReveal} />
          ) : detail?.error ? (
            <div className="field-alert" role="alert">
              The capture's details could not load: {detail.error}
            </div>
          ) : !data && capture.id !== null && capture.id !== undefined ? (
            <p className="field-caption">Loading the capture's details…</p>
          ) : (
            <p className="field-caption">
              No settings dump with this capture. Tick Settings dump in Radar
              rich data on Node Control: the node sends one, once, with its next
              capture.
            </p>
          )}
          {cfg && (
            <p className="field-caption">
              Settings in force at the capture:{" "}
              {CFG.filter(
                ([key]) => cfg[key] !== undefined && cfg[key] !== null,
              )
                .map(
                  ([key, name, unit]) =>
                    `${name} ${cfgValue(key, cfg[key], unit)}`,
                )
                .join(" · ")}
              .
            </p>
          )}
          {data && (
            <details className="field-radar-details">
              <summary>Capture summary and context</summary>
              <pre className="field-raw">
                {JSON.stringify(
                  {
                    summary: data.summary,
                    context,
                    byte_size: data.byte_size,
                    fw: data.fw,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
